import { CircleAlert, Info, TriangleAlert, type LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * AttentionCard (W-2 Home "Needs attention"): a title, one line of detail
 * and exactly one action. The tone sets the icon and the accent border;
 * colour is never the only signal (the icon and the copy carry it too).
 * Every string arrives translated, and the action is the caller's link.
 */

export type AttentionTone = "error" | "warning" | "info";

const TONES: Record<AttentionTone, { icon: LucideIcon; border: string; text: string }> = {
  error: { icon: CircleAlert, border: "border-l-status-error", text: "text-status-error" },
  warning: { icon: TriangleAlert, border: "border-l-status-warning", text: "text-status-warning" },
  info: { icon: Info, border: "border-l-status-info", text: "text-status-info" },
};

export interface AttentionCardProps extends Omit<React.ComponentProps<"article">, "title"> {
  title: string;
  body?: string;
  tone: AttentionTone;
  /** The single action, e.g. a router link styled as a button. */
  action: React.ReactNode;
  /** Heading level inside the page outline (default 3). */
  headingLevel?: 2 | 3 | 4;
}

function AttentionCard({
  title,
  body,
  tone,
  action,
  headingLevel = 3,
  className,
  ...props
}: AttentionCardProps) {
  const id = React.useId();
  const { icon: Icon, border, text } = TONES[tone];
  const Heading = `h${headingLevel}` as "h3";
  return (
    <article
      data-slot="attention-card"
      data-tone={tone}
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex flex-col gap-3 rounded-lg border border-l-4 bg-card p-4 text-card-foreground",
        border,
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-3">
        <Icon aria-hidden="true" className={cn("mt-0.5 size-5 shrink-0", text)} />
        <div className="min-w-0">
          <Heading id={`${id}-title`} className="font-semibold">
            {title}
          </Heading>
          {body ? <p className="mt-1 text-sm text-muted-foreground">{body}</p> : null}
        </div>
      </div>
      <div className="flex">{action}</div>
    </article>
  );
}

export { AttentionCard };
